import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React from "react";
import type { ReactNode } from "react";

interface IResultTableProps {
    columns: string[];
    rows: unknown[];
}

const SUMMARY_FIELDS = 3;

function renderCell(value: unknown): ReactNode {
    if (value === null || value === undefined) return <span style={{ opacity: 0.5 }}>—</span>;
    if (typeof value === "boolean") return value ? "✓" : "✗";
    if (typeof value === "object") {
        return <code style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{JSON.stringify(value)}</code>;
    }
    const text = String(value);
    return <span style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{text}</span>;
}

function summaryText(row: Record<string, unknown>, fields: string[]): string {
    const visible = fields.filter((f) => !f.startsWith("@"));
    return visible
        .slice(0, SUMMARY_FIELDS)
        .map((f) => {
            const v = row[f];
            const s = v === null || v === undefined
                ? "—"
                : typeof v === "object"
                    ? JSON.stringify(v)
                    : String(v);
            return `${f}: ${s}`;
        })
        .join(" · ");
}

export default function ResultTable({ columns, rows }: IResultTableProps) {
    const sharedOrderedFields = React.useMemo(() => {
        if (columns.length === 0) return null;
        const userFields = columns.filter((f) => !f.startsWith("@"));
        const odataFields = columns.filter((f) => f.startsWith("@"));
        return [...userFields, ...odataFields];
    }, [columns]);

    if (rows.length === 0) {
        return <Alert severity="info">No matching records.</Alert>;
    }

    return (
        <Stack spacing={1}>
            {rows.map((row, i) => {
                const r = row as Record<string, unknown>;
                const fields = columns.length > 0 ? columns : Object.keys(r);
                const orderedFields = sharedOrderedFields ?? (() => {
                    const ks = Object.keys(r);
                    const u = ks.filter((f) => !f.startsWith("@"));
                    const o = ks.filter((f) => f.startsWith("@"));
                    return [...u, ...o];
                })();
                return (
                    <Accordion
                        defaultExpanded={i === 0}
                        key={i}
                        slotProps={{ transition: { mountOnEnter: true, unmountOnExit: true } }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography
                                noWrap
                                sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                                variant="body2"
                            >
                                {summaryText(r, fields)}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Table size="small">
                                <TableBody>
                                    {orderedFields.map((f) => (
                                        <TableRow key={f}>
                                            <TableCell sx={{ color: "text.secondary", fontWeight: 500, verticalAlign: "top", width: 220, maxWidth: 220, wordBreak: "break-word" }}>
                                                {f}
                                            </TableCell>
                                            <TableCell sx={{ wordBreak: "break-word" }}>
                                                {renderCell(r[f])}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Stack>
    );
}
